import type { CSSProperties, ReactNode } from 'react'
import type { LockedReason } from './customViewPanel.types'
import { VpIcon } from '@vtmn-play/react'
import { useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BASE } from './customViewPanel.sections'
import './info-tooltip.scss'

type InfoTooltipProps = Readonly<{
  children?: (triggerProps: { 'aria-describedby': string, 'style': CSSProperties }) => ReactNode
  tooltipClassName?: string
}> & (
  | { reason: LockedReason, content?: never }
  | { reason?: never, content: string }
)

export function InfoTooltip({ reason, content, children, tooltipClassName }: InfoTooltipProps) {
  const { t } = useTranslation()
  const id = useId()
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const anchorName = `--tip-${id.replace(/\W/g, '')}`
  const tooltipKey = reason === 'models' ? '.tooltip-only-models' : '.tooltip-only-supermodels'
  const tooltipContent = reason ? t(`${BASE}${tooltipKey}`) : content
  const accessibleDescription = reason ? t(`aria-label.${tooltipKey}`) : content

  const showTooltip = () => tooltipRef.current?.showPopover?.()
  const hideTooltip = () => tooltipRef.current?.hidePopover?.()
  const triggerProps = {
    'aria-describedby': `${id}-a11y`,
    'style': { anchorName } as CSSProperties,
  }

  return (
    <span
      className="custom-view-panel__info-tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children
        ? children(triggerProps)
        : (
            <button
              type="button"
              className="custom-view-panel__info-icon"
              popoverTarget={id}
              popoverTargetAction="toggle"
              {...triggerProps}
              aria-label={t('aria-label.tooltip-info')}
            >
              <VpIcon name="information" aria-hidden="true" />
            </button>
          )}
      <span
        id={id}
        ref={tooltipRef}
        className={tooltipClassName
          ? `custom-view-panel__tooltip ${tooltipClassName}`
          : 'custom-view-panel__tooltip'}
        popover="manual"
        role="tooltip"
        aria-hidden="true"
        style={{ positionAnchor: anchorName } as React.CSSProperties}
      >
        {tooltipContent}
      </span>
      <span id={`${id}-a11y`} hidden>
        {accessibleDescription}
      </span>
    </span>
  )
}
